'use client';

import clsx from 'clsx';
import OriginalImage, { ImageProps } from 'next/image';

import styles from './Image.module.css';

import { Ref, forwardRef, useState } from 'react';

type Props = {
  placeholder?: ImageProps['placeholder'] | 'background';
} & Omit<ImageProps, 'placeholder'>;

const CDNImage = (
  { placeholder = 'background', className, onLoad, onError, ...props }: Props,
  ref: Ref<HTMLImageElement>,
) => {
  const [isError, setIsError] = useState(false);

  const handleOnLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.setAttribute('data-loaded', 'true');
    onLoad?.(event);
  };

  const handleOnError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsError(true);
    onError?.(event);
  };

  if (isError) {
    return <div data-error={true} className={styles.errorImage} />;
  }

  return (
    <OriginalImage
      ref={ref}
      className={clsx([placeholder === 'background' && styles.grayBackground, className])}
      onLoad={handleOnLoad}
      onError={handleOnError}
      placeholder={placeholder === 'background' ? 'empty' : placeholder}
      unoptimized
      {...props}
    />
  );
};

CDNImage.displayName = 'CDNImage';

/**
 * CDN에 있는 이미지를 사용하는 경우
 * - next/image를 사용하여 이미지를 불러옵니다.
 * - viewType에 따라 이미지의 크기를 조절합니다.
 * - resolution에 따라 이미지의 해상도를 조절합니다.
 * - placeholder에 따라 로딩시 기본 이미지를 보여줍니다.
 * - 이미지 로딩이 완료되면 data-loaded 속성을 추가합니다.
 */
export const Image = forwardRef(CDNImage);
